import { memo } from "react";
import styled from "styled-components";
import { StreamCard } from "@sablier/v2-components/molecules";
import { Trans } from "@sablier/v2-locales";
import Hint from "../Hint";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 2);
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 2) 0;

    &[data-visible="true"] {
      display: inherit;
    }
    &:not([data-visible="true"]) {
      display: none;
    }
  }
`;

const Cells = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: calc(${(props) => props.theme.sizes.edge} * 1);
  width: 100%;
`;

const Title = styled.div`
  & > h3 {
    ${(props) => props.theme.styles.textMenu}
    & {
      color: ${(props) => props.theme.colors.gray100};
      span {
        color: ${(props) => props.theme.colors.gray400};
      }
    }
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxXL} {
    width: 100%;
    max-width: 900px;

    ${Cells} {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  ${(props) => props.theme.medias.maxMD} {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    max-width: 600px;
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2) 0;

    ${Cells} {
      grid-template-columns: repeat(2, 1fr);
      grid-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    }
  }
  ${(props) => props.theme.medias.max(420)} {
    ${Cells} {
      ${(props) => props.theme.styles.column}
      & > * {
        min-height: 100%;
      }
    }
  }
`;

interface PropsGrid {
  isVisible?: boolean;
}

function GridGroup({ isVisible = false }: PropsGrid) {
  return (
    <Wrapper data-visible={isVisible}>
      <Title>
        <h3>
          <Trans
            i18nKey={"structs.galleryGroupLinear"}
            components={[<span key={"0"} />]}
          />
        </h3>
      </Title>
      <Cells>
        <StreamCard isGroup isRecommended shape={"linear"} />
        <StreamCard isGroup shape={"cliff"} />
      </Cells>
      <Title>
        <h3>
          <Trans
            i18nKey={"structs.galleryGroupDynamic"}
            components={[<span key={"0"} />]}
          />
        </h3>
      </Title>
      <Cells>
        <StreamCard isGroup shape={"dynamicStepper"} />
        <StreamCard isGroup shape={"dynamicTimelock"} />
        <StreamCard isGroup shape={"dynamicUnlockLinear"} />
        <StreamCard isGroup shape={"dynamicUnlockCliff"} />
        <StreamCard isGroup shape={"dynamicExponential"} />
        <StreamCard isGroup shape={"dynamicCliffExponential"} />
        <Hint isGroup />
      </Cells>
    </Wrapper>
  );
}

function GridSingle({ isVisible = false }: PropsGrid) {
  return (
    <Wrapper data-visible={isVisible}>
      <Title>
        <h3>
          <Trans
            i18nKey={"structs.gallerySingleLinear"}
            components={[<span key={"0"} />]}
          />
        </h3>
      </Title>
      <Cells>
        <StreamCard isRecommended shape={"linear"} />
        <StreamCard shape={"cliff"} />
      </Cells>
      <Title>
        <h3>
          <Trans
            i18nKey={"structs.gallerySingleDynamic"}
            components={[<span key={"0"} />]}
          />
        </h3>
      </Title>
      <Cells>
        <StreamCard shape={"dynamicStepper"} />
        <StreamCard shape={"dynamicTimelock"} />
        <StreamCard shape={"dynamicUnlockLinear"} />
        <StreamCard shape={"dynamicUnlockCliff"} />
        <StreamCard shape={"dynamicExponential"} />
        <StreamCard shape={"dynamicCliffExponential"} />
        <Hint />
      </Cells>
    </Wrapper>
  );
}

interface Props {
  isGroup?: boolean;
}

function Grid({ isGroup = false }: Props) {
  return (
    <>
      <GridSingle isVisible={!isGroup} />
      <GridGroup isVisible={isGroup} />
    </>
  );
}

export default memo(Grid);
